import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  Alert,
  InputAdornment,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  MonetizationOn as PointIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { supabase } from '../services/supabase';
import { format, startOfMonth, endOfMonth } from 'date-fns';

function StatCard({ icon, title, value, color }) {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${color}15`,
          color: color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
      </Box>
    </Paper>
  );
}

function Referrals() {
  const [referredUsers, setReferredUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [referredRes, allUsersRes] = await Promise.all([
        supabase
          .from('users')
          .select('*')
          .not('referred_by', 'is', null)
          .order('created_at', { ascending: false }),
        supabase
          .from('users')
          .select('id, name, email, referral_code'),
      ]);

      if (referredRes.error) throw referredRes.error;
      if (allUsersRes.error) throw allUsersRes.error;

      setReferredUsers(referredRes.data || []);
      setAllUsers(allUsersRes.data || []);
    } catch (err) {
      console.error('Failed to fetch referral data:', err);
      setError('추천 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // Build lookup map: userId -> user info
  const userMap = useMemo(() => {
    const map = {};
    allUsers.forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [allUsers]);

  // Enrich referred users with referrer info
  const enrichedUsers = useMemo(() => {
    return referredUsers.map((u) => {
      const referrer = userMap[u.referred_by] || {};
      return {
        ...u,
        referrer_name: referrer.name || '-',
        referrer_email: referrer.email || '-',
      };
    });
  }, [referredUsers, userMap]);

  // Stats
  const totalReferrals = referredUsers.length;

  const thisMonthReferrals = useMemo(() => {
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    return referredUsers.filter((u) => {
      const created = new Date(u.created_at);
      return created >= monthStart && created <= monthEnd;
    }).length;
  }, [referredUsers]);

  const totalReferralPoints = useMemo(() => {
    // Each referred user's total_points_earned represents points earned through the platform
    // For referral tracking, we count the sum of total_points_earned for all referred users
    return referredUsers.reduce((sum, u) => sum + (u.total_points_earned || 0), 0);
  }, [referredUsers]);

  // Referral code stats: how many signups per referral_code
  const referralCodeStats = useMemo(() => {
    const codeCountMap = {};
    referredUsers.forEach((u) => {
      const referrer = userMap[u.referred_by];
      if (referrer && referrer.referral_code) {
        const code = referrer.referral_code;
        if (!codeCountMap[code]) {
          codeCountMap[code] = {
            id: code,
            referral_code: code,
            referrer_name: referrer.name || '-',
            referrer_email: referrer.email || '-',
            count: 0,
          };
        }
        codeCountMap[code].count += 1;
      }
    });
    return Object.values(codeCountMap).sort((a, b) => b.count - a.count);
  }, [referredUsers, userMap]);

  // Filter by search query (referrer name or email)
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return enrichedUsers;
    const q = searchQuery.toLowerCase();
    return enrichedUsers.filter(
      (u) =>
        u.referrer_name.toLowerCase().includes(q) ||
        u.referrer_email.toLowerCase().includes(q) ||
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q))
    );
  }, [enrichedUsers, searchQuery]);

  const referredColumns = [
    {
      field: 'referrer_name',
      headerName: '추천인',
      width: 150,
    },
    {
      field: 'referrer_email',
      headerName: '추천인 이메일',
      width: 220,
    },
    {
      field: 'name',
      headerName: '피추천인',
      width: 150,
    },
    {
      field: 'email',
      headerName: '피추천인 이메일',
      width: 220,
    },
    {
      field: 'created_at',
      headerName: '가입일',
      width: 160,
      valueFormatter: (value) =>
        value ? format(new Date(value), 'yyyy-MM-dd HH:mm') : '',
    },
  ];

  const codeColumns = [
    {
      field: 'referral_code',
      headerName: '추천 코드',
      width: 150,
    },
    {
      field: 'referrer_name',
      headerName: '추천인',
      width: 150,
    },
    {
      field: 'referrer_email',
      headerName: '추천인 이메일',
      width: 220,
    },
    {
      field: 'count',
      headerName: '가입 수',
      width: 120,
      type: 'number',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        추천 현황
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* 통계 카드 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatCard
            icon={<PeopleIcon fontSize="large" />}
            title="총 추천 수"
            value={totalReferrals.toLocaleString()}
            color="#2D9500"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatCard
            icon={<CalendarIcon fontSize="large" />}
            title="이번 달 추천 수"
            value={thisMonthReferrals.toLocaleString()}
            color="#1976d2"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <StatCard
            icon={<PointIcon fontSize="large" />}
            title="추천 유저 총 포인트"
            value={totalReferralPoints.toLocaleString()}
            color="#ed6c02"
          />
        </Grid>
      </Grid>

      {/* 검색 */}
      <TextField
        fullWidth
        placeholder="추천인 또는 피추천인 이름/이메일로 검색"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          },
        }}
      />

      {/* 추천 현황 DataGrid */}
      <Typography variant="h6" gutterBottom>
        추천 현황 목록
      </Typography>
      <Box sx={{ height: 450, width: '100%', mb: 4 }}>
        {!loading && (
          <DataGrid
            rows={filteredUsers}
            columns={referredColumns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            loading={loading}
          />
        )}
        {!loading && filteredUsers.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">추천 데이터가 없습니다.</Typography>
          </Box>
        )}
      </Box>

      {/* 추천 코드별 통계 */}
      <Typography variant="h6" gutterBottom>
        추천 코드별 통계
      </Typography>
      <Box sx={{ height: 400, width: '100%' }}>
        {!loading && (
          <DataGrid
            rows={referralCodeStats}
            columns={codeColumns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25]}
            disableRowSelectionOnClick
            loading={loading}
          />
        )}
        {!loading && referralCodeStats.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">추천 코드 통계가 없습니다.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Referrals;
